import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useCallback, useMemo, useRef } from 'react'
import { Gift } from 'lucide-react'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { PageTitle } from '@/shared/ui/page-title'
import { useTelegram, useBackButton, useMainButton } from '@/shared/lib/telegram'
import { useToast } from '@/shared/ui/toast'
import { cn } from '@/shared/lib/utils'
import { AmountSelector } from '@/features/gift-cards/ui/amount-selector'
import { GreetingPresets } from '@/features/gift-cards/ui/greeting-presets'
import { SignaturePreview } from '@/features/gift-cards/ui/signature-preview'
import { usePurchaseGiftCard } from '@/features/gift-cards/api/use-purchase-gift-card'

export const Route = createFileRoute('/gift-cards')({
  component: GiftCardsPage,
})

function GiftCardsPage() {
  const navigate = useNavigate()
  const { hapticFeedback, webApp } = useTelegram()
  const { error: showError } = useToast()
  const purchase = usePurchaseGiftCard()

  // Form state
  const [selectedAmount, setSelectedAmount] = useState(2000)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [senderName, setSenderName] = useState('')
  const [personalMessage, setPersonalMessage] = useState('')

  const finalAmount = selectedAmount
  const [emailError, setEmailError] = useState(false)
  const [amountError, setAmountError] = useState(false)

  // ====================================================================
  // Refs — всё через refs чтобы кнопки не дёргались при вводе текста
  // ====================================================================
  const recipientEmailRef = useRef(recipientEmail)
  const recipientNameRef = useRef(recipientName)
  const senderNameRef = useRef(senderName)
  const personalMessageRef = useRef(personalMessage)
  const finalAmountRef = useRef(finalAmount)
  recipientEmailRef.current = recipientEmail
  recipientNameRef.current = recipientName
  senderNameRef.current = senderName
  personalMessageRef.current = personalMessage
  finalAmountRef.current = finalAmount

  const hapticRef = useRef(hapticFeedback)
  const showErrorRef = useRef(showError)
  const purchaseMutateRef = useRef(purchase.mutateAsync)
  hapticRef.current = hapticFeedback
  showErrorRef.current = showError
  purchaseMutateRef.current = purchase.mutateAsync

  // Back button — стабильный ref, не пересоздаётся
  const goHome = useCallback(() => navigate({ to: '/' }), [navigate])
  useBackButton(goHome)

  // Submit — пустые deps, всё через refs
  const handleSubmit = useCallback(async () => {
    const amount = finalAmountRef.current
    const email = recipientEmailRef.current

    if (amount < 500) {
      hapticRef.current.notificationOccurred('error')
      showErrorRef.current('Минимальная сумма — 500 ₽')
      setAmountError(true)
      document.getElementById('amount-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    setAmountError(false)

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      hapticRef.current.notificationOccurred('error')
      showErrorRef.current('Укажите корректный email')
      setEmailError(true)
      document.getElementById('recipient-email')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      document.getElementById('recipient-email')?.focus()
      return
    }
    setEmailError(false)

    hapticRef.current.impactOccurred('medium')

    try {
      const result = await purchaseMutateRef.current({
        initialAmount: amount,
        recipientEmail: email.trim(),
        recipientName: recipientNameRef.current.trim() || undefined,
        senderName: senderNameRef.current.trim() || undefined,
        personalMessage: personalMessageRef.current.trim() || undefined,
        buyerEmail: email.trim(),
        returnUrl: `${window.location.origin}/gift-card-success`,
      })

      if (result.paymentUrl) {
        if (window.Telegram?.WebApp?.openLink) {
          window.Telegram.WebApp.openLink(result.paymentUrl)
        } else {
          window.open(result.paymentUrl, '_blank')
        }
        navigate({ to: '/gift-card-success' })
      }
    } catch (error) {
      hapticRef.current.notificationOccurred('error')
      showErrorRef.current(error instanceof Error ? error.message : 'Ошибка оформления')
    }
  }, [])

  // MainButton — цвета как строки, не объект (стабильные deps)
  const mainButtonText = finalAmount >= 500
    ? 'Подарить сертификат'
    : 'Выберите номинал'

  const isDark = webApp?.colorScheme === 'dark'

  const mainButtonOptions = useMemo(() => ({
    text: mainButtonText,
    onClick: handleSubmit,
    color: isDark ? '#E8632B' : '#C73B0D',
    textColor: '#FFFFFF',
    isActive: finalAmount >= 500,
    isLoading: purchase.isPending,
  }), [mainButtonText, handleSubmit, isDark, finalAmount, purchase.isPending])

  useMainButton(mainButtonOptions)

  return (
    <AppShell showBottomNav>
      <div className="flex-1 bg-[var(--tg-theme-bg-color,#FFFFFF)] overflow-y-auto">
        {/* Header */}
        <div className="px-4 pt-4 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-5 h-5 text-primary" />
            </div>
            <PageTitle title="Подарите чай" subtitle="Сертификат мгновенно придёт на email" />
          </div>
        </div>

        <GiftCardForm
          selectedAmount={selectedAmount}
          recipientEmail={recipientEmail}
          recipientName={recipientName}
          senderName={senderName}
          personalMessage={personalMessage}
          emailError={emailError}
          amountError={amountError}
          onAmountChange={(v) => { setSelectedAmount(v); setAmountError(false) }}
          onRecipientEmailChange={(v) => { setRecipientEmail(v); setEmailError(false) }}
          onRecipientNameChange={setRecipientName}
          onSenderNameChange={setSenderName}
          onPersonalMessageChange={setPersonalMessage}
        />
        <div className="bottom-nav-spacer" />
      </div>
      <BottomNav />
    </AppShell>
  )
}

// ====================================================================
// Form — изолирован от MainButton
// ====================================================================

const INPUT_CLASS = cn(
  'w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all',
  'bg-[var(--tg-theme-secondary-bg-color,#F5F5F5)] text-[var(--tg-theme-text-color,#1A1A1A)]',
  'placeholder:text-[var(--tg-theme-hint-color,#999999)]',
  'border-[var(--tg-theme-hint-color,#999999)]/30',
  'focus:border-[var(--tg-theme-button-color,#1A1A1A)]/50'
)

const LABEL_CLASS = 'block text-xs font-semibold text-[var(--tg-theme-hint-color,#999999)] uppercase tracking-wider mb-2'

interface GiftCardFormProps {
  selectedAmount: number
  recipientEmail: string
  recipientName: string
  senderName: string
  personalMessage: string
  emailError: boolean
  amountError: boolean
  onAmountChange: (amount: number) => void
  onRecipientEmailChange: (value: string) => void
  onRecipientNameChange: (value: string) => void
  onSenderNameChange: (value: string) => void
  onPersonalMessageChange: (value: string) => void
}

function GiftCardForm({
  selectedAmount,
  recipientEmail, recipientName, senderName, personalMessage,
  emailError, amountError,
  onAmountChange,
  onRecipientEmailChange, onRecipientNameChange, onSenderNameChange, onPersonalMessageChange,
}: GiftCardFormProps) {
  return (
    <div className="px-4 space-y-5">
      <section id="amount-section" className={amountError ? 'animate-shake' : ''}>
        <label className={LABEL_CLASS}>Номинал</label>
        <AmountSelector value={selectedAmount} onChange={onAmountChange} />
        {amountError && <p className="text-xs text-red-500 mt-1">Минимум 500 ₽</p>}
      </section>

      <section>
        <label className={LABEL_CLASS}>Получатель</label>
        <div className="space-y-2">
          <input id="recipient-email" type="email" value={recipientEmail} onChange={(e) => onRecipientEmailChange(e.target.value)} placeholder="Email получателя *" required className={cn(INPUT_CLASS, emailError && 'border-red-400 ring-1 ring-red-400/20')} />
          <input type="text" value={recipientName} onChange={(e) => onRecipientNameChange(e.target.value)} placeholder="Имя получателя" className={INPUT_CLASS} />
        </div>
      </section>

      <div className="h-px bg-[var(--tg-theme-hint-color,#999999)]/15" />

      <section>
        <label className={LABEL_CLASS}>От кого</label>
        <input type="text" value={senderName} onChange={(e) => onSenderNameChange(e.target.value)} placeholder="Ваше имя" className={INPUT_CLASS} />
      </section>

      <section>
        <label className={LABEL_CLASS}>Поздравление</label>
        <GreetingPresets value={personalMessage} onChange={onPersonalMessageChange} />
      </section>

      <SignaturePreview senderName={senderName} personalMessage={personalMessage} />
    </div>
  )
}
